import { inject, Injectable } from '@angular/core';
import { FfdComponentStore } from '@libs/utils';
import { PrescriptionV2, PrescriptionsDataService } from '@ffd/api-client';
import { distinctUntilChanged, Observable, switchMap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectIsEaglesoftPms } from '@libs/store';
import { HealthHistoryDialogService } from '../../health-history-dialog.service';

interface PrescriptionsState {
  prescriptions: PrescriptionV2[];
  loading: boolean;
  loaded: boolean;
  error?: string;
}

export interface PrescriptionsVM {
  prescriptions: PrescriptionsItemVM[];
  isLoading: boolean;
}

export type NsStatus = 'standard' | 'non-standard' | 'pending';

export interface PrescriptionsItemVM {
  id: string;
  drugName: string;
  description: string;
  providerId: string;
  prescriptionDate: string;
  nsStatus: NsStatus;
}

@Injectable()
export class PrescriptionsTabService extends FfdComponentStore<PrescriptionsState, PrescriptionsVM> {
  private readonly healthHistoryDialogService = inject(HealthHistoryDialogService);
  private readonly prescriptionDataService = inject(PrescriptionsDataService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);

  private readonly siteId = this.healthHistoryDialogService.siteIdSignal;
  private readonly patientId = this.healthHistoryDialogService.patientIdSignal;
  private readonly isEaglesoft = this.store.selectSignal(selectIsEaglesoftPms);

  public selectPrescriptionsItems = this.selectSignal((state) => state.prescriptions);
  public selectIsPrescriptionsLoading = this.selectSignal((state) => state.loading);
  public selectPrescriptionsCount = this.selectSignal((state) => state.prescriptions.length);

  constructor() {
    super({
      prescriptions: [],
      loading: false,
      loaded: false,
      error: undefined,
    });
  }

  protected getPrescriptions = this.updater((state): PrescriptionsState => {
    return {
      ...state,
      prescriptions: [],
      loading: true,
      loaded: false,
      error: undefined,
    };
  });
  protected getPrescriptionsLoaded = this.updater((state, prescriptions: PrescriptionV2[]): PrescriptionsState => {
    return {
      ...state,
      prescriptions,
      loading: false,
      loaded: true,
      error: undefined,
    };
  });
  protected getPrescriptionsFailed = this.updater((state, error: string): PrescriptionsState => {
    return {
      ...state,
      prescriptions: [],
      loading: false,
      loaded: false,
      error,
    };
  });

  public override init(): void {
    this.loadPrescriptions({ siteId: this.siteId(), patientId: this.patientId() });
  }

  protected override mapStateToViewModel(state: PrescriptionsState): PrescriptionsVM {
    return {
      prescriptions: state.prescriptions.map((prescription) => ({
        id: prescription.id,
        drugName: prescription.drugName ?? '',
        description: prescription.description ?? '',
        providerId: prescription.providerId ?? '',
        prescriptionDate: prescription.prescriptionDate ?? '',
        nsStatus: prescription.standard ? 'standard' : 'non-standard',
      })),
      isLoading: state.loading,
    };
  }

  private readonly loadPrescriptions = this.effect(($: Observable<{ siteId: string; patientId: string }>) =>
    $.pipe(
      distinctUntilChanged(),
      switchMap(({ siteId, patientId }) => {
        this.getPrescriptions();
        return this.prescriptionDataService.getPrescriptionsV2(siteId, patientId).pipe(
          tapResponse(
            (prescriptions) => {
              this.getPrescriptionsLoaded(prescriptions);
            },
            (error: HttpErrorResponse) => {
              if (!this.isEaglesoft()) {
                this.toastService.error(this.translateService.instant('common_error_message'));
              }

              this.getPrescriptionsFailed(error.message);
            },
          ),
        );
      }),
    ),
  );
}
